import { Controller } from "@hotwired/stimulus"

// Dish-list builder for the partner booking form (question 9), as a 3-step modal:
//
//   step 1 — pick how to build the list: import a spreadsheet (.csv/.xlsx/.xls),
//            upload a photo of the menu (read server-side by Gemini), or type
//            the dishes by hand.
//   step 2 — review what came back, fix it, add or drop dishes.
//   step 3 — confirm. The list is written back onto the booking form as nested
//            attributes and summarised in place (total + every dish).
//
// Nothing is persisted here — the dishes ride along with the booking submit.
export default class extends Controller {
  static targets = [
    "modal", "step", "stepDot",
    "fileInput", "imageInput", "status",
    "rows", "rowTemplate", "counter", "reviewError",
    "confirmTotal", "confirmList", "footerBtn",
    "hidden", "summary", "summaryTotal", "summaryList", "empty"
  ]

  static values = {
    importUrl: String,
    analyzeUrl: String,
    min: { type: Number, default: 10 }
  }

  connect() {
    // Re-renders after a failed submit arrive with the hidden inputs already
    // in the DOM — read them back so the summary survives validation errors.
    this.renderSummary(this.readHiddenItems())
  }

  // --- modal plumbing ------------------------------------------------------

  open(event) {
    if (event) event.preventDefault()
    const existing = this.readHiddenItems()
    if (existing.length > 0) {
      this.fillRows(existing)
      this.showStep(1)
    } else {
      this.showStep(0)
    }
    this.setStatus("")
    this.modalInstance().show()
  }

  modalInstance() {
    // bootstrap is loaded from the CDN in the layout, so it lives on window.
    return window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
  }

  showStep(index) {
    this.stepTargets.forEach((pane, i) => { pane.hidden = i !== index })
    this.stepDotTargets.forEach((dot, i) => {
      dot.classList.toggle("is-active", i === index)
      dot.classList.toggle("is-done", i < index)
    })
    // Footer buttons declare their step in data-step, so the actions on offer
    // always match the pane on screen (step 1 has none — you pick a source).
    this.footerBtnTargets.forEach((btn) => {
      btn.hidden = Number(btn.dataset.step) !== index
    })
    this.currentStep = index
  }

  backToStart(event) {
    if (event) event.preventDefault()
    this.showStep(0)
  }

  backToReview(event) {
    if (event) event.preventDefault()
    this.showStep(1)
  }

  // --- step 1: the three ways in -------------------------------------------

  pickFile(event) {
    if (event) event.preventDefault()
    this.fileInputTarget.click()
  }

  pickImage(event) {
    if (event) event.preventDefault()
    this.imageInputTarget.click()
  }

  // Seeds the minimum number of blank rows so the customer can type straight
  // into the list, the way the client's own mock-up asked for it.
  startManual(event) {
    if (event) event.preventDefault()
    this.fillRows(Array.from({ length: this.minValue }, () => ({ external_id: "", name: "" })))
    this.showStep(1)
  }

  async importFile(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    this.setStatus("Leyendo el archivo…")
    await this.loadInto(this.importUrlValue, "file", file, event.currentTarget)
  }

  async analyzeImage(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    this.setStatus("Leyendo el menú con IA — puede tardar de 10 a 30 s…")
    await this.loadInto(this.analyzeUrlValue, "image", file, event.currentTarget)
  }

  async loadInto(url, field, file, input) {
    if (!url) {
      this.setStatus("Endpoint no configurado.", "error")
      return
    }
    const body = new FormData()
    body.append(field, file)

    try {
      const data = await this.postFormData(url, body)
      const items = Array.isArray(data.items) ? data.items : []
      if (items.length === 0) {
        this.setStatus("No encontramos platillos. Intente con otro archivo.", "error")
        return
      }
      this.fillRows(items)
      this.setStatus("")
      this.showStep(1)
    } catch (err) {
      this.setStatus(`No se pudo importar: ${err.message || err}`, "error")
    } finally {
      if (input) input.value = ""
    }
  }

  // --- step 2: review + edit -----------------------------------------------

  fillRows(items) {
    this.rowsTarget.innerHTML = ""
    items.forEach((item) => this.appendRow(item))
    this.syncCount()
  }

  addRow(event) {
    if (event) event.preventDefault()
    const row = this.appendRow({ external_id: "", name: "" })
    row?.querySelector("[data-dish-id]")?.focus()
    this.syncCount()
  }

  appendRow(item) {
    const html = this.rowTemplateTarget.innerHTML
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    const row = this.rowsTarget.lastElementChild
    if (!row) return null
    row.querySelector("[data-dish-id]").value = item.external_id || ""
    row.querySelector("[data-dish-name]").value = item.name || ""
    this.renumber()
    return row
  }

  removeRow(event) {
    event.preventDefault()
    event.currentTarget.closest("[data-dish-row]")?.remove()
    this.renumber()
    this.syncCount()
  }

  renumber() {
    this.rowsTarget.querySelectorAll("[data-dish-index]").forEach((el, i) => {
      el.textContent = `${i + 1}.`
    })
  }

  // Rows with an empty name are ignored — a half-filled row is not a dish.
  collectRows() {
    return Array.from(this.rowsTarget.querySelectorAll("[data-dish-row]"))
      .map((row) => ({
        external_id: row.querySelector("[data-dish-id]")?.value.trim() || "",
        name: row.querySelector("[data-dish-name]")?.value.trim() || ""
      }))
      .filter((item) => item.name.length > 0)
  }

  syncCount() {
    const count = this.collectRows().length
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} / ${this.minValue} mínimo`
      this.counterTarget.classList.toggle("is-ok", count >= this.minValue)
    }
    return count
  }

  // --- step 3: confirm -----------------------------------------------------

  toConfirm(event) {
    if (event) event.preventDefault()
    const items = this.collectRows()

    if (items.length < this.minValue) {
      this.setReviewError(`Faltan platillos: informe al menos ${this.minValue} (lleva ${items.length}).`)
      return
    }
    this.setReviewError("")

    this.pendingItems = items
    this.confirmTotalTarget.textContent = items.length
    this.confirmListTarget.innerHTML = items
      .map((item, i) => `
        <li class="dish-confirm__row">
          <span class="dish-confirm__num">${i + 1}</span>
          <span class="dish-confirm__id">${this.escape(item.external_id) || "—"}</span>
          <span class="dish-confirm__name">${this.escape(item.name)}</span>
        </li>`)
      .join("")
    this.showStep(2)
  }

  confirm(event) {
    if (event) event.preventDefault()
    const items = this.pendingItems || this.collectRows()
    this.writeHiddenItems(items)
    this.renderSummary(items)
    this.modalInstance().hide()
  }

  // --- the booking form's own state ----------------------------------------

  // One hidden input pair per dish — ordinary Rails nested attributes, so the
  // controller needs no special parsing on submit.
  writeHiddenItems(items) {
    this.hiddenTarget.innerHTML = items
      .map((item, i) => `
        <input type="hidden" name="shooting[menu_items_attributes][${i}][external_id]" value="${this.escapeAttr(item.external_id)}">
        <input type="hidden" name="shooting[menu_items_attributes][${i}][name]" value="${this.escapeAttr(item.name)}">
        <input type="hidden" name="shooting[menu_items_attributes][${i}][position]" value="${i}">`)
      .join("")
  }

  readHiddenItems() {
    const items = []
    this.hiddenTarget.querySelectorAll('input[name$="[name]"]').forEach((input) => {
      const prefix = input.name.replace(/\[name\]$/, "")
      const idInput = this.hiddenTarget.querySelector(`input[name="${prefix}[external_id]"]`)
      items.push({ external_id: idInput?.value || "", name: input.value })
    })
    return items
  }

  renderSummary(items) {
    const has = items.length > 0
    this.emptyTarget.hidden = has
    this.summaryTarget.hidden = !has
    if (!has) return

    this.summaryTotalTarget.textContent = items.length
    this.summaryListTarget.innerHTML = items
      .map((item, i) => `
        <li class="dish-summary__row">
          <span class="dish-summary__num">${i + 1}</span>
          <span class="dish-summary__id">${this.escape(item.external_id) || "—"}</span>
          <span class="dish-summary__name">${this.escape(item.name)}</span>
        </li>`)
      .join("")
  }

  // --- helpers -------------------------------------------------------------

  async postFormData(url, formData) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    const resp = await fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": token || "" },
      body: formData,
      credentials: "same-origin"
    })
    const json = await resp.json().catch(() => ({}))
    if (!resp.ok) throw new Error(json.error || `HTTP ${resp.status}`)
    return json
  }

  setStatus(message, tone = "neutral") {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.style.color = tone === "error" ? "var(--piktiz-pink)" : ""
  }

  setReviewError(message) {
    if (!this.hasReviewErrorTarget) return
    this.reviewErrorTarget.textContent = message
    this.reviewErrorTarget.hidden = message.length === 0
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }

  escapeAttr(value) {
    return this.escape(value).replace(/"/g, "&quot;")
  }
}
